import { onIdTokenChanged, type User } from 'firebase/auth';
import { useSyncExternalStore } from 'react';
import { firebase } from '../lib/firebase';

/**
 * Who is signed in and which household/role the server granted (custom claims).
 *  - signed-out:   show login (or pairing on a wall)
 *  - no-household: signed in, but not part of a family yet → setup wizard / invite code
 *  - ready:        everything available
 */
export type Role = 'parent' | 'teen' | 'kid' | 'device';

export type Session =
  | { status: 'loading' }
  | { status: 'signed-out' }
  | { status: 'no-household'; user: User }
  | { status: 'ready'; user: User; householdId: string; role: Role; memberId?: string };

let session: Session = { status: 'loading' };
const listeners = new Set<() => void>();
let started = false;

function start() {
  if (started) return;
  started = true;
  onIdTokenChanged(firebase().auth, async (user) => {
    if (!user) {
      session = { status: 'signed-out' };
    } else {
      const { claims } = await user.getIdTokenResult();
      session =
        typeof claims.householdId === 'string' && typeof claims.role === 'string'
          ? {
              status: 'ready',
              user,
              householdId: claims.householdId,
              role: claims.role as Role,
              memberId: typeof claims.memberId === 'string' ? claims.memberId : undefined,
            }
          : { status: 'no-household', user };
    }
    listeners.forEach((l) => l());
  });
}

export function useSession(): Session {
  start();
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => session,
  );
}

/** After a callable changed the claims (household created, device paired …). */
export async function refreshClaims() {
  const user = firebase().auth.currentUser;
  if (user) await user.getIdToken(true);
}
